import React from 'react';
import { Link } from 'react-router-dom';
import { useCursor } from '../context/CursorContext';
import { AnimatedSection, StaggerChildren } from '../components/AnimatedSection';
import { AnimatedCounter } from '../components/AnimatedCounter';
import { TiltCard } from '../components/TiltCard';
import { howItWorks, featuredCreators as fallbackFeatured, stats as fallbackStats } from '../data/mock';
import { useYouTubeCreators, formatCount } from '../hooks/useYouTube';

export default function ForBrandsPage() {
  const { setLabel } = useCursor();
  const { data: ytData } = useYouTubeCreators();

  const agg = ytData?.aggregated;
  const liveStats = agg
    ? [
        { value: formatCount(agg.total_subscribers), label: 'Combined Subscribers' },
        { value: formatCount(agg.total_views), label: 'Total Views' },
        { value: String(agg.total_creators), label: 'Creators' },
        { value: formatCount(agg.total_videos), label: 'Videos Published' },
      ]
    : fallbackStats;

  const liveFeatured = ytData?.creators?.filter((c) => c.featured);
  const featuredCreators = liveFeatured && liveFeatured.length > 0 ? liveFeatured : fallbackFeatured;

  return (
    <div className="min-h-screen pt-32 pb-20 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Hero */}
        <AnimatedSection className="text-center mb-20">
          <p className="text-green-500 font-semibold uppercase tracking-[0.3em] mb-3 text-xs">For Brands</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4">
            GET YOUR BRAND IN FRONT OF<br />THE RIGHT CRYPTO AUDIENCE
          </h1>
          <p className="text-white/40 max-w-2xl mx-auto mb-8">
            We connect Web3 brands with vetted creators who have real, engaged communities.
          </p>
          <Link
            to="/get-started"
            className="group relative inline-block px-10 py-4 bg-green-500 text-black font-bold uppercase tracking-wider text-sm hover:bg-green-400 transition-colors duration-300 overflow-hidden"
            data-testid="brands-start-campaign-cta"
            onMouseEnter={() => setLabel('Start')}
            onMouseLeave={() => setLabel('')}
          >
            <span className="relative z-10">Start Your Campaign</span>
            <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </Link>
        </AnimatedSection>

        {/* How It Works */}
        <AnimatedSection className="mb-20">
          <div className="text-center mb-12">
            <p className="text-green-500 font-semibold uppercase tracking-[0.3em] mb-3 text-xs">Process</p>
            <h2 className="text-3xl md:text-4xl font-black text-white">HOW IT WORKS</h2>
          </div>
          <StaggerChildren className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" staggerDelay={150}>
            {howItWorks.map((item, i) => (
              <TiltCard key={i} cursorLabel={item.step} className="p-6 border border-white/5 bg-white/[0.02] hover:border-green-500/30">
                <div className="w-8 h-8 border border-green-500/30 flex items-center justify-center mb-4">
                  <span className="text-green-500 text-xs font-black">{item.step}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-white/40 text-sm">{item.description}</p>
              </TiltCard>
            ))}
          </StaggerChildren>
        </AnimatedSection>

        {/* Network Stats */}
        <AnimatedSection className="mb-20">
          <div className="text-center mb-12">
            <p className="text-green-500 font-semibold uppercase tracking-[0.3em] mb-3 text-xs">Our Network</p>
            <h2 className="text-3xl md:text-4xl font-black text-white">THE REACH YOU GET</h2>
          </div>
          <StaggerChildren className="grid grid-cols-2 md:grid-cols-4 gap-4" staggerDelay={150}>
            {liveStats.map((stat, i) => (
              <TiltCard key={i} cursorLabel="Stat" className="text-center p-8 border border-green-500/20 bg-green-500/5 hover:border-green-500/40">
                <AnimatedCounter value={stat.value} className="text-3xl md:text-4xl font-black text-white mb-1 block" />
                <p className="text-white/40 uppercase tracking-wider text-[10px]">{stat.label}</p>
              </TiltCard>
            ))}
          </StaggerChildren>
        </AnimatedSection>

        {/* Featured Creators */}
        <AnimatedSection className="mb-20">
          <div className="text-center mb-12">
            <p className="text-green-500 font-semibold uppercase tracking-[0.3em] mb-3 text-xs">Your Partners</p>
            <h2 className="text-3xl md:text-4xl font-black text-white">CREATORS YOU'LL WORK WITH</h2>
          </div>
          <StaggerChildren className="grid md:grid-cols-3 gap-4" staggerDelay={200}>
            {featuredCreators.map((creator) => (
              <TiltCard
                key={creator.handle}
                cursorLabel="View"
                glowColor="rgba(34, 197, 94, 0.2)"
                className="p-6 border border-green-500/30 bg-green-500/5 hover:border-green-500/50"
              >
                {creator.thumbnail ? (
                  <img src={creator.thumbnail} alt={creator.display_name} className="w-12 h-12 rounded-full mb-4 border border-green-500/30" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-4">
                    <span className="text-lg font-black text-black">{creator.display_name[0]}</span>
                  </div>
                )}
                <h3 className="text-xl font-black text-white mb-1">{creator.display_name}</h3>
                <p className="text-green-500 text-sm font-semibold mb-2">{formatCount(creator.subscriber_count)} subscribers</p>
                <p className="text-white/50 text-sm">{creator.description}</p>
              </TiltCard>
            ))}
          </StaggerChildren>
          <div className="text-center mt-8">
            <Link to="/creators" className="text-green-500 text-sm font-semibold uppercase tracking-wider hover:text-green-400 transition-colors">
              View All Creators &rarr;
            </Link>
          </div>
        </AnimatedSection>

        {/* CTA */}
        <AnimatedSection>
          <div className="p-12 border border-green-500/20 bg-gradient-to-b from-green-500/10 to-transparent text-center">
            <h2 className="text-3xl font-black text-white mb-3">Ready to Start?</h2>
            <p className="text-white/50 mb-8">Tell us about your brand and we'll create a proposal.</p>
            <Link
              to="/get-started"
              className="group relative inline-block px-10 py-4 bg-green-500 text-black font-bold uppercase tracking-wider text-sm hover:bg-green-400 transition-colors duration-300 overflow-hidden"
              onMouseEnter={() => setLabel('Start')}
              onMouseLeave={() => setLabel('')}
            >
              <span className="relative z-10">Start Your Campaign</span>
              <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </Link>
          </div>
        </AnimatedSection>
      </div>
    </div>
  );
}
